"use client";

import { useAuth } from "@/components/AuthContext";
import { bindGuestVoiceSignup } from "@/lib/experiments/guestVoiceRuntimeClient";
import {
  GUEST_SESSION_CLAIM_EVENT,
  claimGuestSession,
  clearPendingGuestSessionClaim,
  isNewAccountForGuestClaim,
  readPendingGuestSessionClaim,
  registerActiveGuestSessionClaim,
  removeClaimedLocalConversation,
} from "@/lib/guestSessionClaim";
import { createLogger } from "@/lib/logger";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";

const logger = createLogger("guest-session-claim");

export function GuestSessionClaimCoordinator() {
  const { user, isLoading } = useAuth();
  const posthog = usePostHog();
  const attemptedRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || !user) return;
    // Independent of transcript/summary availability. First session issuance
    // also invokes the same server binding, so this effect is not a routing race.
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const bind = async () => {
      try {
        await bindGuestVoiceSignup();
      } catch {
        if (!cancelled && ++attempts < 3) retry = setTimeout(bind, 1500 * attempts);
        else logger.warn("Guest voice binding deferred to next session");
      }
    };
    void bind();
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
    };
  }, [isLoading, user?.id]);

  useEffect(() => {
    if (isLoading || !user) return;
    const pending = readPendingGuestSessionClaim();
    if (!pending || attemptedRef.current === `${user.id}:${pending.claimId}`) return;

    // A transcript can be private on a shared browser. Only a new account created
    // directly from this staged guest flow is claimed automatically.
    if (!isNewAccountForGuestClaim(user.created_at, pending.stagedAt)) return;

    attemptedRef.current = `${user.id}:${pending.claimId}`;
    posthog?.capture("guest_session_claim_attempted", {
      claim_source: "guest_demo_signup",
      language_name: pending.languageName,
      tutor_turn_count: pending.messages.filter((message) => message.role === "assistant").length,
      learner_turn_count: pending.messages.filter((message) => message.role === "user").length,
    });

    const claim = registerActiveGuestSessionClaim(claimGuestSession(pending));
    void claim
      .then((result) => {
        removeClaimedLocalConversation(pending.localConversationId);
        if (result.summaryReady || result.summaryStatus === "skipped") {
          clearPendingGuestSessionClaim();
        }
        window.dispatchEvent(
          new CustomEvent(GUEST_SESSION_CLAIM_EVENT, {
            detail: {
              localConversationId: pending.localConversationId,
              conversationId: result.conversationId,
              summaryReady: result.summaryReady,
            },
          })
        );
        posthog?.capture("guest_session_claim_succeeded", {
          claim_source: "guest_demo_signup",
          already_claimed: result.alreadyClaimed,
          summary_ready: result.summaryReady,
        });
      })
      .catch((error) => {
        attemptedRef.current = null;
        logger.warn("Guest session claim failed; local copy retained for retry", {
          error: error instanceof Error ? error.message : String(error),
        });
        posthog?.capture("guest_session_claim_failed", {
          claim_source: "guest_demo_signup",
          failure_stage: "claim_request",
        });
      });
  }, [isLoading, posthog, user]);

  return null;
}
